import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Phone, ChevronDown } from "lucide-react";
import { BRAND, waLink } from "../../lib/mockData";
import { useCart } from "../../lib/cartContext";
import { useCategories } from "../../hooks/useCatalog";

// Static nav items. Categories is NOT listed here — it is rendered from the
// Firestore `categories` collection so admin renames/additions flow through
// automatically (a hardcoded list is what previously left "Corporate" in the
// nav after the client renamed it to "Home-Sweet-Home Hampers").
const NAV_LINKS = [
  { id: "home", label: "Home", to: "/" },
  { id: "shop", label: "Shop", to: "/shop" },
  { id: "track", label: "Track Order", to: "/track" },
  { id: "about", label: "About us", to: "/about" },
  { id: "categories", label: "Categories", dropdown: true },
  { id: "faq", label: "FAQ", to: "/faq" },
  { id: "contact", label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [desktopCatsOpen, setDesktopCatsOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const { itemCount } = useCart();
  const { data: categories = [] } = useCategories();
  const location = useLocation();
  const catsRef = useRef(null);

  const isCategoryRoute = location.pathname.startsWith("/category/");

  useEffect(() => {
    setOpen(false);
    setDesktopCatsOpen(false);
    setMobileCatsOpen(false);
  }, [location.pathname]);

  // Close the desktop dropdown on Escape or an outside click.
  useEffect(() => {
    if (!desktopCatsOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setDesktopCatsOpen(false);
    const onDown = (e) => {
      if (catsRef.current && !catsRef.current.contains(e.target)) setDesktopCatsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [desktopCatsOpen]);

  return (
    <header
      className="sticky top-0 z-50 bg-cream/95 backdrop-blur-md border-b border-gold/25"
      data-testid="site-header"
    >
      {/* Top brand strip */}
      <div className="bg-emerald text-cream text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 opacity-90">
            <span className="hidden sm:inline">Islandwide delivery in Singapore</span>
            <span className="sm:hidden">Delivery across Singapore</span>
            <span className="text-gold">•</span>
            <span className="hidden md:inline">Halal-friendly premium gift hampers</span>
          </div>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            className="flex items-center gap-1.5 hover:text-gold transition-colors"
            data-testid="header-phone-link"
          >
            <Phone size={12} />
            <span>{BRAND.phone}</span>
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 md:h-28 gap-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0"
            data-testid="header-logo-link"
          >
            <img
              src={BRAND.logo}
              alt={BRAND.name}
              className="h-24 md:h-28 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-emerald">
            {/* Categories uses click-to-toggle, not hover: a hover-open fights
                the click handler and breaks entirely on touch devices. */}
            {NAV_LINKS.map((l) =>
              l.dropdown ? (
                <div key={l.id} ref={catsRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setDesktopCatsOpen((v) => !v)}
                    aria-expanded={desktopCatsOpen}
                    aria-haspopup="true"
                    className={`nav-underline inline-flex items-center gap-1 transition-colors hover:text-gold ${
                      isCategoryRoute ? "text-gold active" : ""
                    }`}
                    data-testid="nav-link-categories"
                  >
                    {l.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${desktopCatsOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {desktopCatsOpen && (
                    <div
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
                      data-testid="nav-categories-dropdown"
                    >
                      <div className="w-64 bg-cream border border-gold/30 rounded-lg shadow-xl py-2 max-h-[70vh] overflow-y-auto">
                        {categories.length === 0 ? (
                          <div className="px-4 py-2.5 text-sm text-emerald-light/60">Loading…</div>
                        ) : (
                          categories.map((c) => (
                            <NavLink
                              key={c.slug}
                              to={`/category/${c.slug}`}
                              className={({ isActive }) =>
                                `block px-4 py-2.5 text-sm transition-colors hover:bg-gold/10 hover:text-gold ${
                                  isActive ? "text-gold" : "text-emerald"
                                }`
                              }
                              data-testid={`nav-category-${c.slug}`}
                            >
                              {c.name}
                            </NavLink>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={l.id}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `nav-underline transition-colors hover:text-gold ${isActive ? "text-gold active" : ""}`
                  }
                  data-testid={`nav-link-${l.id}`}
                >
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/cart"
              className="relative p-2.5 text-emerald hover:text-gold transition-colors"
              aria-label="Cart"
              data-testid="cart-toggle-btn"
            >
              <ShoppingBag size={22} strokeWidth={1.6} />
              {itemCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-gold text-emerald text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  data-testid="cart-count-badge"
                >
                  {itemCount}
                </span>
              )}
            </Link>

            <a
              href={waLink("Hi HALALGIFHAMPERS, I'd like to order via WhatsApp.")}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 bg-emerald text-cream px-5 py-2.5 text-sm font-medium hover:bg-emerald-dark transition-colors"
              data-testid="header-whatsapp-cta"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1s-.8.9-1 1.1c-.2.2-.4.2-.6.1-.3-.1-1.2-.4-2.4-1.5-.9-.8-1.5-1.8-1.7-2-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5s-.7-1.6-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1 1-1 2.4 1 2.9 1.2 3.1c.1.2 2 3 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.5-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.6 1.4 5.2L2 22l4.9-1.3c1.5.8 3.2 1.3 5.1 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2z"/>
              </svg>
              Order via WhatsApp
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-emerald hover:text-gold transition-colors"
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {open && (
        <div
          className="lg:hidden border-t border-gold/20 bg-cream"
          data-testid="mobile-nav"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) =>
              l.dropdown ? (
                <div key={l.id}>
                  <button
                    type="button"
                    onClick={() => setMobileCatsOpen((v) => !v)}
                    aria-expanded={mobileCatsOpen}
                    className={`w-full flex items-center justify-between px-3 py-3 text-sm font-medium transition-colors hover:bg-emerald/5 ${
                      isCategoryRoute ? "text-gold" : "text-emerald"
                    }`}
                    data-testid="mobile-nav-link-categories"
                  >
                    {l.label}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${mobileCatsOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {mobileCatsOpen && (
                    <div
                      className="pl-3 border-l border-gold/30 ml-3 mb-1"
                      data-testid="mobile-nav-categories"
                    >
                      {categories.length === 0 ? (
                        <div className="px-3 py-2.5 text-sm text-emerald-light/60">Loading…</div>
                      ) : (
                        categories.map((c) => (
                          <NavLink
                            key={c.slug}
                            to={`/category/${c.slug}`}
                            className={({ isActive }) =>
                              `block px-3 py-2.5 text-sm transition-colors hover:bg-emerald/5 ${
                                isActive ? "text-gold" : "text-emerald-light"
                              }`
                            }
                            data-testid={`mobile-nav-category-${c.slug}`}
                          >
                            {c.name}
                          </NavLink>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={l.id}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-3 text-sm font-medium transition-colors hover:bg-emerald/5 ${isActive ? "text-gold" : "text-emerald"}`
                  }
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </NavLink>
              )
            )}
            <a
              href={waLink("Hi HALALGIFHAMPERS, I'd like to order via WhatsApp.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 bg-emerald text-cream px-4 py-3 text-sm font-medium text-center hover:bg-emerald-dark transition-colors"
              data-testid="mobile-whatsapp-cta"
            >
              Order via WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
